import { useState, useEffect, useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { storeIds } from '../../../../shared/constants/store';
import { getStoreMenu } from '../../../../shared/repos/graphql/store';
import { giftingCategoryMenuMockItems } from './GiftingCategoryMenuBrandRefresh.mocks';
import { GIFTING_CATEGORY_CONFIG } from './giftingCategoryMenuConstants';
import {
  buildGiftingCategoryMenuItems,
  getMergedProductsForGiftingConfig,
  isGiftingSampleMenu,
  resolveGiftingMenuStoreId,
} from './giftingCategoryMenuHelpers';

/** Loads menuV2 for gifting tabs + carousel; merges slugs per giftingCategoryMenuConstants. */
export function useGiftingCategoryMenu() {
  const selectedStore = useSelector((state) => state.user?.selectedStore);
  const isShowOosProducts = useSelector(
    (state) => state.product?.isShowOosProducts || false
  );
  const [activeCategoryId, setActiveCategoryId] = useState(
    giftingCategoryMenuMockItems[0]?.id
  );
  const [menuV2, setMenuV2] = useState(null);
  const [loading, setLoading] = useState(true);
  const [menuError, setMenuError] = useState(false);

  const usesSampleMenu = isGiftingSampleMenu(selectedStore, storeIds.sampleMenu);
  const menuStoreId = resolveGiftingMenuStoreId(
    selectedStore,
    storeIds.sampleMenu
  );

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setMenuError(false);

    getStoreMenu(menuStoreId, isShowOosProducts)
      .then((response) => {
        if (cancelled) return;
        const nextMenu = response?.data?.store?.menuV2;
        if (!nextMenu || !Array.isArray(nextMenu)) {
          setMenuV2(null);
          setMenuError(true);
          return;
        }
        setMenuV2(nextMenu);
      })
      .catch(() => {
        if (!cancelled) {
          setMenuV2(null);
          setMenuError(true);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [menuStoreId, isShowOosProducts]);

  const categoryItems = useMemo(
    () => buildGiftingCategoryMenuItems(menuV2, giftingCategoryMenuMockItems),
    [menuV2]
  );

  const activeCategory = useMemo(
    () =>
      categoryItems.find((item) => item.id === activeCategoryId) ||
      categoryItems[0],
    [categoryItems, activeCategoryId]
  );

  const categoryProducts = useMemo(() => {
    if (!activeCategory) return [];

    if (menuV2) {
      const config = GIFTING_CATEGORY_CONFIG.find(
        (entry) => entry.id === activeCategory.id
      );
      if (config) {
        const fromMenu = getMergedProductsForGiftingConfig(menuV2, config);
        if (fromMenu.length) return fromMenu;
      }
    }

    if (activeCategory.products?.length) {
      return activeCategory.products;
    }

    return [];
  }, [activeCategory, menuV2]);

  const selectCategory = useCallback((item) => {
    if (item?.id) setActiveCategoryId(item.id);
  }, []);

  return {
    categoryItems,
    activeCategoryId: activeCategory?.id,
    activeCategory,
    categoryProducts,
    selectCategory,
    loading,
    menuError,
    usesSampleMenu,
    menuStoreId,
  };
}

export default useGiftingCategoryMenu;
